import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/design-system/Button'
import { Card, Skeleton } from '@/design-system/primitives'
import { cn } from '@/lib/cn'
import { formatMoney } from '@/lib/money'

import { useAdminMutation, useAdminQuery } from '../api'
import { AdminHeader } from '../AdminLayout'
import { useAdminContext } from '../session'
import type { Page } from '../useCollection'
import type { ExpenseCategory, I18n, ShoppingItem, ShoppingSummary } from '../types'

/**
 * La liste de courses : ce qu'il reste à acheter, par poste.
 *
 * Elle vit à côté des dépenses sans se confondre avec elles. Une dépense
 * constate ce qui est **sorti** ; cette liste dit ce qui doit **entrer**.
 * Écrire « 40 bouteilles d'eau » n'engage rien, et c'est pourquoi le total
 * d'ici n'entre jamais dans le budget consommé — il se lit en face, comme un
 * repère pour décider.
 *
 * Trois choix portent l'écran :
 *
 * * **Le prix est unitaire.** « Quantité 3, montant 12 € » est ambigu, et
 *   l'ambiguïté se paie devant le ticket de caisse. On saisit le prix lu sur
 *   l'étiquette, le total se calcule.
 * * **On coche.** Une liste de courses qu'on ne peut pas cocher ne sert à rien
 *   dans un magasin ; le serveur note qui a coché et quand.
 * * **Une ligne se supprime sans cérémonie.** Rien n'a été engagé — c'est
 *   toute la différence avec une dépense, qui s'annule et laisse sa trace.
 */
export function AdminShoppingPage() {
  const { t, i18n } = useTranslation()
  const { eventId } = useAdminContext()
  const [hideBought, setHideBought] = useState(false)

  useEffect(() => {
    document.title = t('admin.section.shopping')
  }, [t])

  const base = `/admin/events/${eventId}/shopping`
  const label = (value: I18n) => value[i18n.language] || value[Object.keys(value)[0] ?? ''] || '—'

  const items = useAdminQuery<ShoppingItem[]>(['admin-shopping', eventId], base, eventId)
  const summary = useAdminQuery<ShoppingSummary>(
    ['admin-shopping-summary', eventId, i18n.language],
    `${base}/summary?locale=${i18n.language}`,
    eventId,
  )
  const categories = useAdminQuery<Page<ExpenseCategory>>(
    ['admin-expense-categories', eventId],
    `/admin/events/${eventId}/finance/expense-categories`,
    eventId,
  )

  const invalidate = [
    ['admin-shopping', eventId],
    ['admin-shopping-summary', eventId, i18n.language],
  ]
  const create = useAdminMutation<ShoppingItem, Record<string, unknown>>(
    'post', () => base, eventId, invalidate,
  )
  const save = useAdminMutation<ShoppingItem, { id: string } & Record<string, unknown>>(
    'patch', (variables) => `${base}/${variables.id}`, eventId, invalidate,
  )
  const remove = useAdminMutation<{ message: string }, { id: string }>(
    'delete', (variables) => `${base}/${variables.id}`, eventId, invalidate,
  )

  const currency = summary.data?.currency ?? 'EUR'
  const money = (minor: number) => formatMoney({ amount_minor: minor, currency }, i18n.language)
  const categoryList = categories.data?.items ?? []
  const names = new Map(categoryList.map((row) => [row.id, label(row.name_i18n)]))

  const rows = (items.data ?? []).filter((row) => !hideBought || !row.is_bought)

  // Groupé par poste, dans l'ordre du résumé — le plus lourd d'abord, c'est
  // la ligne sur laquelle on arbitre.
  const order = (summary.data?.lines ?? []).map((line) => line.category_id)
  const groups = [...new Set(rows.map((row) => row.category_id))].sort(
    (a, b) => order.indexOf(a) - order.indexOf(b),
  )

  return (
    <>
      <AdminHeader
        title={t('admin.section.shopping')}
        description={t('admin.shopping.description')}
      />

      {summary.isPending ? <Skeleton className="h-24 w-full" /> : null}

      {summary.data ? (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <Figure
            label={t('admin.shopping.toBuy')}
            value={money(summary.data.planned_minor - summary.data.bought_minor)}
            hint={t('admin.shopping.remainingItems', {
              count: summary.data.remaining_items,
            })}
            tone="champagne"
          />
          <Figure label={t('admin.shopping.planned')} value={money(summary.data.planned_minor)} />
          <Figure label={t('admin.shopping.bought')} value={money(summary.data.bought_minor)} />
          <Figure
            label={t('admin.shopping.lines')}
            value={String(summary.data.items)}
            hint={t('admin.shopping.notBudget')}
          />
        </div>
      ) : null}

      <QuickAdd
        categories={categoryList}
        label={label}
        busy={create.isPending}
        error={create.isError ? create.error.message : null}
        onCreate={(payload) => create.mutate(payload)}
      />

      <div className="mb-4 mt-6 flex flex-wrap items-center gap-3">
        <Button
          variant={hideBought ? 'outline' : 'ghost'}
          size="sm"
          onClick={() => setHideBought((current) => !current)}
        >
          {t('admin.shopping.hideBought')}
        </Button>
        {remove.isError ? (
          <span className="text-sm text-ember-300">{remove.error.message}</span>
        ) : null}
      </div>

      {items.isPending ? <Skeleton className="h-64 w-full" /> : null}

      {rows.length === 0 && !items.isPending ? (
        <p className="text-sm text-content-muted">{t('admin.shopping.empty')}</p>
      ) : null}

      <div className="space-y-6">
        {groups.map((categoryId) => {
          const line = summary.data?.lines.find((row) => row.category_id === categoryId)
          const group = rows.filter((row) => row.category_id === categoryId)

          return (
            <Card key={categoryId ?? 'none'}>
              <div className="flex flex-wrap items-baseline justify-between gap-3">
                <h2 className="font-display text-heading-md text-content-primary">
                  {categoryId
                    ? names.get(categoryId) ?? t('admin.shopping.unclassified')
                    : t('admin.shopping.unclassified')}
                </h2>
                <span className="text-sm text-content-secondary">
                  <strong className="tabular-nums text-champagne-300">
                    {money(line?.planned_minor ?? 0)}
                  </strong>
                  {/* Le budget du poste en face, jamais additionné : l'un est
                      une limite, l'autre une liste. C'est en les voyant côte à
                      côte qu'on arbitre. */}
                  {line?.budget_minor != null ? (
                    <span
                      className={cn(
                        'ml-2 text-caption',
                        line.planned_minor > line.budget_minor
                          ? 'text-ember-300'
                          : 'text-content-muted',
                      )}
                    >
                      {t('admin.shopping.ofBudget', { amount: money(line.budget_minor) })}
                    </span>
                  ) : null}
                </span>
              </div>

              <ul className="mt-4 divide-y divide-line-subtle">
                {group.map((item) => (
                  <ItemRow
                    key={item.id}
                    item={item}
                    categories={categoryList}
                    label={label}
                    money={money}
                    busy={save.isPending || remove.isPending}
                    onSave={(payload) => save.mutate({ id: item.id, ...payload })}
                    onRemove={() => remove.mutate({ id: item.id })}
                  />
                ))}
              </ul>
            </Card>
          )
        })}
      </div>
    </>
  )
}

function Figure({
  label,
  value,
  hint,
  tone = 'neutral',
}: {
  label: string
  value: string
  hint?: string
  tone?: 'neutral' | 'champagne'
}) {
  return (
    <div className="rounded-lg border border-line-subtle bg-bg-elevated px-5 py-4">
      <p className="overline text-content-muted">{label}</p>
      <p
        className={cn(
          'mt-1 font-display text-heading-lg tabular-nums',
          tone === 'champagne' ? 'text-champagne-300' : 'text-content-primary',
        )}
      >
        {value}
      </p>
      {hint ? <p className="mt-1 text-caption text-content-muted">{hint}</p> : null}
    </div>
  )
}

/** Ajouter en une ligne : c'est ainsi qu'on note une course, debout. */
function QuickAdd({
  categories,
  label,
  busy,
  error,
  onCreate,
}: {
  categories: ExpenseCategory[]
  label: (value: I18n) => string
  busy: boolean
  error: string | null
  onCreate: (payload: Record<string, unknown>) => void
}) {
  const { t } = useTranslation()
  const [form, setForm] = useState({ label: '', quantity: '1', price: '', category: '' })

  return (
    <form
      className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-5"
      onSubmit={(event) => {
        event.preventDefault()
        const price = Number.parseFloat(form.price.replace(',', '.') || '0')
        const quantity = Number.parseInt(form.quantity, 10)
        if (!form.label.trim() || !Number.isFinite(quantity) || quantity <= 0) return
        onCreate({
          label: form.label.trim(),
          quantity,
          // Le serveur ne manipule que des unités mineures : un centime perdu
          // dans un flottant se retrouve dans le total.
          unit_price_minor: Number.isFinite(price) ? Math.round(price * 100) : 0,
          category_id: form.category || null,
        })
        setForm({ label: '', quantity: '1', price: '', category: form.category })
      }}
    >
      <input
        className={cn(INPUT, 'lg:col-span-2')}
        placeholder={t('admin.shopping.labelField')}
        value={form.label}
        onChange={(event) => setForm({ ...form, label: event.target.value })}
      />
      <input
        className={cn(INPUT, 'tabular-nums')}
        type="number"
        min={1}
        placeholder={t('admin.shopping.quantity')}
        value={form.quantity}
        onChange={(event) => setForm({ ...form, quantity: event.target.value })}
      />
      <input
        className={cn(INPUT, 'tabular-nums')}
        inputMode="decimal"
        placeholder={t('admin.shopping.unitPrice')}
        value={form.price}
        onChange={(event) => setForm({ ...form, price: event.target.value })}
      />
      <div className="flex gap-2">
        <select
          className={cn(INPUT, 'min-w-0 flex-1')}
          value={form.category}
          onChange={(event) => setForm({ ...form, category: event.target.value })}
        >
          <option value="">{t('admin.shopping.unclassified')}</option>
          {categories.map((row) => (
            <option key={row.id} value={row.id}>
              {label(row.name_i18n)}
            </option>
          ))}
        </select>
        <Button type="submit" size="sm" disabled={busy || !form.label.trim()}>
          {t('admin.shopping.add')}
        </Button>
      </div>

      {error ? (
        <p className="text-sm text-ember-300 sm:col-span-2 lg:col-span-5">{error}</p>
      ) : null}
    </form>
  )
}

function ItemRow({
  item,
  categories,
  label,
  money,
  busy,
  onSave,
  onRemove,
}: {
  item: ShoppingItem
  categories: ExpenseCategory[]
  label: (value: I18n) => string
  money: (minor: number) => string
  busy: boolean
  onSave: (payload: Record<string, unknown>) => void
  onRemove: () => void
}) {
  const { t, i18n } = useTranslation()
  const [editing, setEditing] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [form, setForm] = useState({
    label: item.label,
    quantity: String(item.quantity),
    price: (item.unit_price_minor / 100).toFixed(2),
    category: item.category_id ?? '',
    supplier: item.supplier ?? '',
  })

  if (editing) {
    return (
      <li className="py-3">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <input
            className={cn(INPUT, 'lg:col-span-2')}
            value={form.label}
            onChange={(event) => setForm({ ...form, label: event.target.value })}
          />
          <input
            className={cn(INPUT, 'tabular-nums')}
            type="number"
            min={1}
            value={form.quantity}
            onChange={(event) => setForm({ ...form, quantity: event.target.value })}
          />
          <input
            className={cn(INPUT, 'tabular-nums')}
            inputMode="decimal"
            value={form.price}
            onChange={(event) => setForm({ ...form, price: event.target.value })}
          />
          <select
            className={INPUT}
            value={form.category}
            onChange={(event) => setForm({ ...form, category: event.target.value })}
          >
            <option value="">{t('admin.shopping.unclassified')}</option>
            {categories.map((row) => (
              <option key={row.id} value={row.id}>
                {label(row.name_i18n)}
              </option>
            ))}
          </select>
          <input
            className={cn(INPUT, 'lg:col-span-2')}
            placeholder={t('admin.finance.supplier')}
            value={form.supplier}
            onChange={(event) => setForm({ ...form, supplier: event.target.value })}
          />
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          <Button
            size="sm"
            disabled={busy || !form.label.trim()}
            onClick={() => {
              const quantity = Number.parseInt(form.quantity, 10)
              const price = Number.parseFloat(form.price.replace(',', '.') || '0')
              if (!Number.isFinite(quantity) || quantity <= 0) return
              onSave({
                label: form.label.trim(),
                quantity,
                unit_price_minor: Number.isFinite(price) ? Math.round(price * 100) : 0,
                category_id: form.category || null,
                supplier: form.supplier.trim() || null,
              })
              setEditing(false)
            }}
          >
            {t('admin.project.save')}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>
            {t('admin.project.cancel')}
          </Button>
        </div>
      </li>
    )
  }

  return (
    <li className="flex flex-wrap items-center gap-3 py-2.5">
      <input
        type="checkbox"
        checked={item.is_bought}
        disabled={busy}
        aria-label={t('admin.shopping.markBought', { name: item.label })}
        onChange={(event) => onSave({ is_bought: event.target.checked })}
        className="h-5 w-5 shrink-0 accent-champagne-400"
      />

      <span className={cn('min-w-40 flex-1', item.is_bought && 'opacity-50')}>
        <span
          className={cn(
            'block text-sm text-content-primary',
            item.is_bought && 'line-through',
          )}
        >
          {item.label}
        </span>
        <span className="block text-caption text-content-muted">
          {t('admin.shopping.unitLine', {
            quantity: item.quantity,
            price: money(item.unit_price_minor),
          })}
          {item.supplier ? ` · ${item.supplier}` : ''}
          {item.is_bought && item.bought_at
            ? ` · ${t('admin.shopping.boughtBy', {
                who: item.bought_by_label ?? '—',
                at: new Date(item.bought_at).toLocaleDateString(i18n.language),
              })}`
            : ''}
        </span>
      </span>

      <span className="shrink-0 tabular-nums text-sm text-champagne-300">
        {money(item.total_minor)}
      </span>

      {confirming ? (
        <span className="flex shrink-0 gap-1.5">
          <Button size="sm" disabled={busy} onClick={onRemove}>
            {t('admin.project.deleteYes')}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>
            {t('admin.project.cancel')}
          </Button>
        </span>
      ) : (
        <span className="flex shrink-0 gap-1">
          <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
            {t('admin.shopping.edit')}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setConfirming(true)}>
            {t('admin.shopping.remove')}
          </Button>
        </span>
      )}
    </li>
  )
}

const INPUT =
  'h-10 w-full rounded-md border border-line bg-bg-base px-3 text-sm text-content-primary'
